import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashApi, type MetricContract, type MetricEntry } from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";

const CONTRACT_TONE: Record<string, string> = {
  passing: "text-emerald-300", warning: "text-amber-300", failing: "text-rose-300",
  insufficient_data: "text-slate-400", not_applicable: "text-slate-500",
};
const FIN_TONE: Record<string, string> = {
  business_sales: "text-sky-300", settled_revenue: "text-emerald-300", expected_revenue: "text-amber-300",
  repair_estimate: "text-fuchsia-300", buyback: "text-fuchsia-300", operational: "text-slate-300", none: "text-slate-500",
};

export default function MetricCataloguePage() {
  const cat = useQuery({ queryKey: ["metric-catalogue"], queryFn: dashApi.metricCatalogue });
  const con = useQuery({ queryKey: ["metric-contracts"], queryFn: dashApi.metricContracts });
  const drift = useQuery({ queryKey: ["metric-drift"], queryFn: dashApi.metricDrift });
  const [open, setOpen] = useState<string | null>(null);
  if (cat.isLoading || con.isLoading) return <LoadingState />;
  if (cat.isError) return <ErrorState error={cat.error} />;
  const contractBy: Record<string, MetricContract> = {};
  (con.data?.contracts ?? []).forEach((c) => (contractBy[c.code] = c));
  const cs = con.data?.summary ?? {};
  return (
    <div className="space-y-6">
      <PageHeader
        title="Metric Catalogue, Contracts & Drift"
        description="Every high-value executive number, traced and classified. Financial classes never cross-contaminate: a repair estimate/BuyBack is never business sales, and expected/pending/failed renewals are never settled revenue. Actual/estimated/target/forecast/scenario are kept distinct. No raw source payload, query or credential is exposed."
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Metrics" value={cat.data!.metrics.length} />
        <Stat label="Contracts passing" value={cs.passing ?? 0} />
        <Stat label="Warning" value={cs.warning ?? 0} />
        <Stat label="Failing" value={cs.failing ?? 0} sub={(cs.failing ?? 0) > 0 ? "needs attention" : undefined} />
        <Stat label="Drift findings" value={drift.data?.count ?? 0} />
      </div>

      {(drift.data?.findings.length ?? 0) > 0 && (
        <Panel title="Data drift" subtitle="Schema/classification change vs known contracts — not normal business variation.">
          <ul className="space-y-1 text-xs">
            {drift.data!.findings.map((f, i) => (
              <li key={i} className="flex flex-wrap gap-2">
                <span className={f.severity === "critical" ? "text-rose-300" : "text-amber-300"}>{f.kind}</span>
                <span className="text-slate-500">{f.source}</span>
                <span className="text-slate-300">{f.detail}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Catalogue">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="pb-1 pr-3">Metric</th><th className="pb-1 pr-3">Financial class</th><th className="pb-1 pr-3">Value</th>
              <th className="pb-1 pr-3">Sources</th><th className="pb-1 pr-3">Contract</th><th className="pb-1">History from</th>
            </tr></thead>
            <tbody>
              {cat.data!.metrics.map((m: MetricEntry) => {
                const c = contractBy[m.code];
                return (
                  <Fragment key={m.code}>
                    <tr className="cursor-pointer border-t border-slate-800 hover:bg-slate-900/40" onClick={() => setOpen(open === m.code ? null : m.code)}>
                      <td className="py-1.5 pr-3 text-slate-200">{open === m.code ? "▾" : "▸"} {m.name}<div className="text-[10px] text-slate-600">{m.code}</div></td>
                      <td className={`py-1.5 pr-3 ${FIN_TONE[m.financial_class] ?? "text-slate-400"}`}>{m.financial_class}</td>
                      <td className="py-1.5 pr-3 text-slate-400">{m.value_class}</td>
                      <td className="py-1.5 pr-3 text-slate-500">{m.sources.join(", ")}</td>
                      <td className={`py-1.5 pr-3 ${CONTRACT_TONE[c?.state ?? ""] ?? "text-slate-400"}`}>{c?.state ?? "—"}</td>
                      <td className="py-1.5 text-slate-500">{m.earliest_history ?? "—"}</td>
                    </tr>
                    {open === m.code && (
                      <tr className="border-t border-slate-800/50 bg-slate-900/30">
                        <td colSpan={6} className="p-3 text-[11px] text-slate-400">
                          <p className="text-slate-300">{m.definition}</p>
                          <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3">
                            <div><span className="text-slate-500">Grain:</span> {m.grain}</div>
                            <div><span className="text-slate-500">Unit:</span> {m.unit}</div>
                            <div><span className="text-slate-500">Formula v:</span> {m.formula_version}</div>
                            <div><span className="text-slate-500">Supported scope:</span> {m.supported_scope.join(", ") || "—"}</div>
                            <div><span className="text-slate-500">Unsupported:</span> {m.unsupported_scope.join(", ") || "none"}</div>
                            <div><span className="text-slate-500">Seeded-only:</span> {m.seeded_only ? "yes" : "no"}</div>
                          </div>
                          {m.prohibited_with.length > 0 && <p className="mt-1 text-fuchsia-300/80">Never combined with: {m.prohibited_with.join(", ")}</p>}
                          {m.source_limitations && <p className="mt-1 text-slate-500">Limitations: {m.source_limitations}</p>}
                          {c && c.reasons.length > 0 && <ul className="mt-1 space-y-0.5">{c.reasons.map((r, i) => <li key={i} className="text-amber-300/80">• {r}</li>)}</ul>}
                          <p className="mt-1 text-[10px] text-slate-600">Links: <a href="/validation/kpis" className="text-sky-400">KPI validation</a> · <a href="/acceptance" className="text-sky-400">Acceptance</a> · <a href="/trust" className="text-sky-400">Trust</a></p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
      {cat.data!.metrics.length === 0 && <EmptyState>No metrics.</EmptyState>}
      <p className="text-[11px] text-slate-600">{cat.data!.note}</p>
    </div>
  );
}
